import { useState } from 'react';
import Modal from '../base/Modal';
import Input from '../base/Input';
import Button from '../base/Button';

interface PropertyPDFRequestProps {
  isOpen: boolean;
  onClose: () => void;
  property: any;
}

export default function PropertyPDFRequest({ isOpen, onClose, property }: PropertyPDFRequestProps) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const generateProfessionalPDF = async (pdfData: any) => {
    try {
      const { jsPDF } = await import('https://esm.sh/jspdf@2.5.1');
      
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;
      
      // Colores según la plantilla
      const darkHeader = [45, 55, 72]; // Gris oscuro del header
      const accentColor = [59, 130, 246]; // Azul de acentos
      const textColor = [31, 41, 55]; // Gris oscuro para texto
      const lightBg = [249, 250, 251]; // Fondo claro

      // === HEADER OSCURO (Replicando la plantilla) ===
      doc.setFillColor(...darkHeader);
      doc.rect(0, 0, pageWidth, 35, 'F');

      // Logo y nombre de la empresa en el header
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('LOGO GRUPO FR', 15, 15);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Bienes Raíces Comerciales e Industriales', 15, 22);

      // Título del inmueble en el header (lado derecho)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(pdfData.property.title, 90);
      doc.text(titleLines[0], pageWidth - 15, 12, { align: 'right' });
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Tipo • ${pdfData.property.available_for_sale ? 'Venta' : 'Renta'}`, pageWidth - 15, 18, { align: 'right' });
      doc.text(`• Ciudad, Estado`, pageWidth - 15, 22, { align: 'right' });

      yPosition = 45;

      // === FOTOGRAFÍA PRINCIPAL (Grande, como en la plantilla) ===
      // Nota: En la plantilla real, aquí iría la imagen
      doc.setFillColor(220, 220, 220);
      doc.rect(15, yPosition, 85, 60, 'F');
      
      doc.setTextColor(120, 120, 120);
      doc.setFontSize(10);
      doc.text('FOTOGRAFÍA PRINCIPAL', 57.5, yPosition + 30, { align: 'center' });
      
      // === PRECIO DESTACADO (Lado derecho, como en la plantilla) ===
      const priceBoxX = 105;
      const priceBoxY = yPosition;
      const priceBoxWidth = pageWidth - priceBoxX - 15;
      const priceBoxHeight = 30;
      
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.rect(priceBoxX, priceBoxY, priceBoxWidth, priceBoxHeight, 'FD');
      
      doc.setTextColor(...textColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('PRECIO', priceBoxX + 5, priceBoxY + 8);
      
      doc.setFontSize(16);
      doc.setTextColor(34, 197, 94); // Verde
      let priceText = '';
      if (pdfData.property.available_for_sale && pdfData.property.sale_price) {
        priceText = formatPrice(pdfData.property.sale_price, pdfData.property.currency);
      } else if (pdfData.property.available_for_rent && pdfData.property.rent_price) {
        priceText = formatPrice(pdfData.property.rent_price, pdfData.property.currency);
      }
      doc.text(priceText, priceBoxX + 5, priceBoxY + 18);
      
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      if (pdfData.property.available_for_sale) {
        doc.text(`• Venta / Renta  • ${pdfData.property.area || 0} m²`, priceBoxX + 5, priceBoxY + 24);
      }
      doc.text(`• Mantenimiento: ${pdfData.property.maintenance_fee ? formatPrice(pdfData.property.maintenance_fee, pdfData.property.currency) : '$0'} / m²`, priceBoxX + 5, priceBoxY + 28);

      // === CARACTERÍSTICAS PRINCIPALES (Tabla como en la plantilla) ===
      yPosition = priceBoxY + priceBoxHeight + 5;
      
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.rect(priceBoxX, yPosition, priceBoxWidth, 30, 'FD');
      
      doc.setTextColor(...textColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('CARACTERÍSTICAS PRINCIPALES', priceBoxX + 5, yPosition + 7);
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      let charY = yPosition + 12;
      
      const characteristics = [
        { label: 'Tipo:', value: pdfData.property.property_type || 'N/A' },
        { label: 'Superficie construida:', value: `${pdfData.property.area || 0} m²` },
        { label: 'Terreno:', value: `${pdfData.property.area || 0} m²` },
        { label: 'Recámaras:', value: pdfData.property.bedrooms?.toString() || 'N/A' },
        { label: 'Baños:', value: pdfData.property.bathrooms?.toString() || 'N/A' },
        { label: 'Estacionamientos:', value: pdfData.property.parking?.toString() || 'N/A' },
        { label: 'Nivel / Piso:', value: pdfData.property.floor?.toString() || 'N/A' },
        { label: 'Antigüedad:', value: pdfData.property.year_built ? `${new Date().getFullYear() - pdfData.property.year_built} años` : 'N/A' },
        { label: 'Climatización:', value: 'N/A' },
        { label: 'Estatus:', value: pdfData.property.status === 'available' ? 'Disponible' : pdfData.property.status },
        { label: 'Uso de suelo:', value: 'N/A' }
      ];
      
      characteristics.forEach((char, index) => {
        if (index < 6) {
          doc.setFont('helvetica', 'normal');
          doc.text(`• ${char.label}`, priceBoxX + 5, charY);
          doc.setFont('helvetica', 'bold');
          doc.text(char.value, priceBoxX + 30, charY);
          charY += 4;
        }
      });

      // === FOTOS ADICIONALES (Miniaturas) ===
      yPosition += 35;
      
      const thumbWidth = 25;
      const thumbHeight = 18;
      const thumbSpacing = 3;
      
      for (let i = 0; i < 3; i++) {
        const thumbX = 15 + (i * (thumbWidth + thumbSpacing));
        doc.setFillColor(220, 220, 220);
        doc.rect(thumbX, yPosition, thumbWidth, thumbHeight, 'F');
        doc.setTextColor(120, 120, 120);
        doc.setFontSize(7);
        doc.text(`Foto ${i + 2}`, thumbX + thumbWidth/2, yPosition + thumbHeight/2, { align: 'center' });
      }

      yPosition += thumbHeight + 10;

      // === DESCRIPCIÓN CORTA ===
      doc.setFillColor(...lightBg);
      doc.rect(15, yPosition, pageWidth - 30, 20, 'F');
      
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DESCRIPCIÓN CORTA', 20, yPosition + 6);
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const shortDescLines = doc.splitTextToSize(
        pdfData.property.short_description || 'Resumen atractivo de 2-3 líneas destacando lo más importante del inmueble.',
        pageWidth - 40
      );
      doc.text(shortDescLines, 20, yPosition + 12);
      
      yPosition += 25;

      // === DESCRIPCIÓN DETALLADA ===
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('DESCRIPCIÓN DETALLADA', 15, yPosition);
      
      yPosition += 6;
      
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.rect(15, yPosition, pageWidth - 30, 35, 'FD');
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      const descLines = doc.splitTextToSize(
        pdfData.property.description || 'Incluye aquí una descripción amplia del inmueble: distribución, acabados, amenidades del edificio, tipo de zona, potencial de uso (oficinas, vivier local comercial, etc.) y cualquier diferenciador relevante.',
        pageWidth - 40
      );
      doc.text(descLines.slice(0, 10), 20, yPosition + 5);
      
      yPosition += 40;

      // === UBICACIÓN ===
      doc.setTextColor(...textColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('UBICACIÓN', 15, yPosition);
      
      yPosition += 6;
      
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(200, 200, 200);
      doc.rect(15, yPosition, pageWidth - 30, 20, 'FD');
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Dirección completa del inmueble.', 20, yPosition + 6);
      doc.text(`${pdfData.property.location}`, 20, yPosition + 11);
      if (pdfData.property.address) {
        doc.text(`${pdfData.property.address}`, 20, yPosition + 16);
      }
      doc.text('Colonia • Ciudad • Estado • C.P.', 20, yPosition + 16);

      yPosition += 25;

      // Verificar si necesitamos nueva página
      if (yPosition > pageHeight - 50) {
        doc.addPage();
        yPosition = 20;
      }

      // === CARACTERÍSTICAS Y AMENIDADES ===
      if (pdfData.property.features && pdfData.property.features.length > 0) {
        doc.setTextColor(...textColor);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('CARACTERÍSTICAS Y AMENIDADES', 15, yPosition);
        
        yPosition += 6;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        
        pdfData.property.features.forEach((feature: string, index: number) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          
          const col = index % 2;
          const xPos = 20 + (col * 90);
          
          doc.setTextColor(34, 197, 94);
          doc.text('✓', xPos, yPosition);
          doc.setTextColor(...textColor);
          doc.text(feature, xPos + 5, yPosition);
          
          if (col === 1) {
            yPosition += 5;
          }
        });
        
        yPosition += 10;
      }

      // === SERVICIOS INCLUIDOS ===
      if (pdfData.property.included_services && pdfData.property.included_services.length > 0) {
        if (yPosition > pageHeight - 40) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setTextColor(...textColor);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('SERVICIOS INCLUIDOS', 15, yPosition);
        
        yPosition += 6;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        
        pdfData.property.included_services.forEach((service: string) => {
          if (yPosition > pageHeight - 20) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setTextColor(34, 197, 94);
          doc.text('✓', 20, yPosition);
          doc.setTextColor(...textColor);
          doc.text(service, 25, yPosition);
          yPosition += 5;
        });
        
        yPosition += 5;
      }

      // === FOOTER (Replicando la plantilla) ===
      const footerY = pageHeight - 35;
      
      doc.setFillColor(...darkHeader);
      doc.rect(0, footerY, pageWidth, 35, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('GRUPO FR VENTURE', pageWidth / 2, footerY + 8, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(`Tel: (+52) 000 000 0000  •  WhatsApp: (+52) 000 000 0000  •  contacto@grupofrventure.com`, pageWidth / 2, footerY + 14, { align: 'center' });
      doc.text('Edificio Skyworks oficina 406 Temozon Norte, Mérida, Yucatán, México', pageWidth / 2, footerY + 19, { align: 'center' });
      doc.text('www.grupofrventure.com  •  Oficina: (+52) 000 000 0000', pageWidth / 2, footerY + 24, { align: 'center' });

      const fileName = `Ficha_Tecnica_${pdfData.property.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      doc.save(fileName);

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Error al generar el PDF. Por favor intenta nuevamente.');
    }
  };

  const formatPrice = (price: number, currency: string = 'MXN') => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/property-pdf-generator-enhanced`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          email,
          propertyId: property.id,
          propertyTitle: property.title,
          propertyPrice: property.sale_price || property.rent_price,
          propertyLocation: property.location,
          propertyImage: property.images?.[0],
          requestType: property.available_for_sale ? 'sale' : 'rent'
        })
      });

      if (!response.ok) {
        throw new Error('Error al procesar la solicitud');
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Error al generar la ficha técnica');
      }

      const pdfData = {
        property: {
          title: property.title,
          location: property.location,
          address: property.address,
          short_description: property.short_description,
          description: property.description,
          property_type: property.property_type,
          sale_price: property.sale_price,
          rent_price: property.rent_price,
          available_for_sale: property.available_for_sale,
          available_for_rent: property.available_for_rent,
          currency: property.currency,
          area: property.area,
          bedrooms: property.bedrooms,
          bathrooms: property.bathrooms,
          parking: property.parking,
          floor: property.floor,
          year_built: property.year_built,
          furnished: property.furnished,
          features: property.features || [],
          included_services: property.included_services || [],
          images: property.images || [],
          google_maps_url: property.google_maps_url
        },
        recipient: {
          email: email,
          date: new Date().toLocaleDateString('es-MX', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })
        },
        company: {
          name: 'Grupo FR Venture',
          email: 'contacto@grupofrventure.com',
          phone: '+52 (999) 123-4567',
          whatsapp: '+52 (999) 123-4567',
          address: 'Mérida, Yucatán, México'
        }
      };
      
      await generateProfessionalPDF(pdfData);

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setEmail('');
      }, 3000);

    } catch (err: any) {
      setError(err.message || 'Error al generar la ficha técnica. Por favor intenta nuevamente.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Descargar Ficha Técnica PDF">
      <div className="p-6">
        {success ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              ¡PDF Generado Exitosamente!
            </h3>
            <p className="text-gray-600 mb-4">
              La ficha técnica en PDF se ha descargado automáticamente. Revisa tu carpeta de descargas.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <i className="ri-information-line mr-2"></i>
                El archivo PDF contiene toda la información de la propiedad con diseño profesional.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">{property.title}</h4>
                <p className="text-sm text-gray-600 mb-1">
                  <i className="ri-map-pin-line mr-1"></i>
                  {property.location}
                </p>
                <p className="text-lg font-bold text-blue-600">
                  {formatPrice(property.sale_price || property.rent_price || 0, property.currency)}
                </p>
              </div>

              <p className="text-gray-700 mb-4">
                Ingresa tu correo electrónico para registrar tu interés y descargar la ficha técnica profesional en PDF.
              </p>

              <Input
                type="email"
                label="Correo Electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                icon={<i className="ri-mail-line"></i>}
              />

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {error}
                </div>
              )}

              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <i className="ri-file-pdf-line mr-2"></i>
                  Se generará un PDF profesional con toda la información de la propiedad y se descargará automáticamente.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="flex-1"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Generando PDF...
                  </>
                ) : (
                  <>
                    <i className="ri-file-pdf-line mr-2"></i>
                    Descargar PDF
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
